import { getApiErrorStatus, hasApiErrorCode, unwrapApiError } from '@/utils/apiError'
import { ProfessionalDocumentsErrorCode } from '@/types/professional-documents.types'
import { QualificationErrorCode } from '@/types/qualification-eligibility.types'
import type {
  EligibilityReason,
  QualificationErrorDetail
} from '@/types/qualification-eligibility.types'

const CODES = Object.values(QualificationErrorCode) as QualificationErrorCode[]

/** Código de qualificação do 422 (axios cru ou `ServiceError`), ou `null`. */
export function getQualificationErrorCode(error: unknown): QualificationErrorCode | null {
  return CODES.find((code) => hasApiErrorCode(error, code)) ?? null
}

type QualificationBody = { missing?: unknown; details?: unknown }

function getBody(error: unknown): QualificationBody | undefined {
  return (unwrapApiError(error) as { response?: { data?: QualificationBody } } | null)?.response
    ?.data
}

/** `details[]` do 422 de lote (`{ stationId, stationDate, userId, missing }` por item, A3.7/A8). */
export function getQualificationDetails(error: unknown): QualificationErrorDetail[] {
  if (!getQualificationErrorCode(error)) return []
  const details = getBody(error)?.details
  return Array.isArray(details) ? (details as QualificationErrorDetail[]) : []
}

/**
 * `missing[]` do 422 de qualificação. Sem `missing` no topo (lote), une os
 * `details[].missing` sem repetir `field`+`key`. Lista vazia quando o erro é outro
 * ou o backend não mandou nada (a UI cai no texto de fallback).
 */
export function getQualificationMissing(error: unknown): EligibilityReason[] {
  if (!getQualificationErrorCode(error)) return []
  const missing = getBody(error)?.missing
  if (Array.isArray(missing) && missing.length) return missing as EligibilityReason[]
  const merged = new Map<string, EligibilityReason>()
  getQualificationDetails(error).forEach((detail) =>
    (detail.missing ?? []).forEach((reason) => merged.set(`${reason.field}:${reason.key}`, reason))
  )
  return [...merged.values()]
}

/**
 * 404 `PROFESSIONAL_DOCUMENTS_DISABLED`: a flag do backend está desligada e todas as rotas
 * novas (documentos, fila, Kanban) respondem assim, mesmo sem token. A tela trata como
 * "feature indisponível", nunca como erro.
 */
export function isProfessionalDocumentsDisabled(error: unknown): boolean {
  return (
    getApiErrorStatus(error) === 404 &&
    hasApiErrorCode(error, ProfessionalDocumentsErrorCode.PROFESSIONAL_DOCUMENTS_DISABLED)
  )
}
